import { supabase } from '@/lib/supabase';
import type { FilterState } from '@/types/lead-attribution';
import type {
  FranchiseAnalytics,
  CityAnalytics,
  TimeAnalytics,
} from '../types';

/**
 * Fetch franchise analytics using SQL aggregation (RPC function)
 * This is 10-100x faster than downloading all records and processing in JS
 *
 * @see supabase/migrations/20251119_add_analytics_aggregation_functions.sql
 */
export const fetchFranchiseAnalytics = async (
  filters: FilterState
): Promise<FranchiseAnalytics[]> => {
  try {
    console.log('🔄 Fetching franchise analytics via RPC...');

    // Use SQL aggregation function instead of downloading all records
    const { data, error } = await supabase.rpc('get_franchise_analytics', {
      p_time_frame: filters.timeFrame || null,
      p_city: filters.city || null,
    });

    if (error) {
      console.error('RPC error, falling back to client-side:', error);
      // Fallback to limited client-side processing if RPC not yet deployed
      return fetchFranchiseAnalyticsFallback(filters);
    }

    // Also fetch monthly trends for the time analytics
    const { data: trends } = await supabase.rpc('get_monthly_trends', {
      p_franchise: null,
      p_months: 12,
    });

    // Map RPC results to expected format
    return (data || []).map((row: {
      franchise: string;
      total_requests: number;
      converted_count: number;
      conversion_rate: number;
    }) => ({
      franchise: row.franchise,
      totalRequests: row.total_requests,
      totalValue: row.converted_count,
      avgValue: row.total_requests > 0 ? row.converted_count / row.total_requests : 0,
      conversionRate: row.conversion_rate || 0,
      timeAnalytics: buildTimeAnalyticsFromTrends(trends || [], row.franchise),
    }));
  } catch (error) {
    console.error('Error fetching franchise analytics:', error);
    throw error;
  }
};

/**
 * Fallback function if RPC not yet deployed - uses limited data
 */
async function fetchFranchiseAnalyticsFallback(
  filters: FilterState
): Promise<FranchiseAnalytics[]> {
  // Only fetch last 500 records max as fallback
  let query = supabase
    .from('requests')
    .select('franchise, status, jobber_created_at')
    .order('jobber_created_at', { ascending: false })
    .limit(500);

  if (filters.timeFrame && filters.timeFrame !== 'all') {
    const now = new Date();
    let startDate: Date;
    switch (filters.timeFrame) {
      case 'last7days':
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case 'last30days':
        startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        break;
      case 'last90days':
        startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
        break;
      default:
        startDate = new Date(now.getFullYear(), 0, 1);
    }
    query = query.gte('jobber_created_at', startDate.toISOString());
  }

  const { data: requests, error } = await query;
  if (error) throw error;

  // Simple aggregation with limited data
  const franchiseMap = new Map<string, { count: number; converted: number }>();
  (requests || []).forEach((r) => {
    const f = r.franchise || 'unknown';
    if (!franchiseMap.has(f)) franchiseMap.set(f, { count: 0, converted: 0 });
    const data = franchiseMap.get(f)!;
    data.count++;
    if (r.status === 'completed' || r.status === 'converted') data.converted++;
  });

  return Array.from(franchiseMap.entries()).map(([franchise, data]) => ({
    franchise,
    totalRequests: data.count,
    totalValue: data.converted,
    avgValue: data.count > 0 ? data.converted / data.count : 0,
    conversionRate: data.count > 0 ? (data.converted / data.count) * 100 : 0,
    timeAnalytics: { weekly: [], monthly: [], yearly: [] },
  }));
}

/**
 * Build time analytics from monthly trends RPC data
 */
function buildTimeAnalyticsFromTrends(
  trends: Array<{ month: string; franchise: string; request_count: number }>,
  franchise: string
): TimeAnalytics {
  const franchiseTrends = trends.filter((t) => t.franchise === franchise);

  return {
    weekly: [], // Weekly requires more data - skip for now
    monthly: franchiseTrends.map((t) => ({
      month: t.month,
      count: t.request_count,
      value: 0,
    })),
    yearly: Object.entries(
      franchiseTrends.reduce((acc, t) => {
        const year = t.month.substring(0, 4);
        acc[year] = (acc[year] || 0) + t.request_count;
        return acc;
      }, {} as Record<string, number>)
    ).map(([year, count]) => ({ year, count, value: 0 })),
  };
}

/**
 * Fetch city analytics using SQL aggregation (RPC function)
 *
 * @see supabase/migrations/20251119_add_analytics_aggregation_functions.sql
 */
export const fetchCityAnalytics = async (
  filters: FilterState
): Promise<CityAnalytics[]> => {
  try {
    console.log('🔄 Fetching city analytics via RPC...');

    // Use SQL aggregation function instead of downloading all records
    const { data, error } = await supabase.rpc('get_city_analytics', {
      p_franchise: filters.franchise || null,
      p_time_frame: filters.timeFrame || null,
      p_city_filter: filters.city || null,
      p_limit: 100,
    });

    if (error) {
      console.error('RPC error, falling back to client-side:', error);
      return fetchCityAnalyticsFallback(filters);
    }

    // Map RPC results to expected format
    return (data || []).map((row: {
      city: string;
      province: string;
      postal_code: string | null;
      franchise: string;
      total_requests: number;
      converted_count: number;
      latest_date: string;
    }) => ({
      city: row.city,
      province: row.province,
      postalCode: row.postal_code || undefined,
      franchise: row.franchise,
      totalRequests: row.total_requests,
      totalValue: row.converted_count,
      timeAnalytics: {
        weekly: [],
        monthly: [{
          month: row.latest_date ? new Date(row.latest_date).toISOString().substring(0, 7) : '',
          count: row.total_requests,
          value: 0,
        }],
        yearly: [],
      },
    }));
  } catch (error) {
    console.error('Error fetching city analytics:', error);
    throw error;
  }
};

/**
 * Fallback function if RPC not yet deployed - uses limited data
 */
async function fetchCityAnalyticsFallback(
  filters: FilterState
): Promise<CityAnalytics[]> {
  // Only fetch last 500 records max as fallback
  let query = supabase
    .from('requests')
    .select('service_city, service_province, service_zip, franchise, status')
    .not('service_city', 'is', null)
    .order('jobber_created_at', { ascending: false })
    .limit(500);

  if (filters.franchise && filters.franchise !== 'all') {
    query = query.eq('franchise', filters.franchise);
  }

  const { data: requests, error } = await query;
  if (error) throw error;

  // Simple aggregation with limited data
  const cityMap = new Map<string, {
    city: string;
    province: string;
    postalCode?: string;
    franchise: string;
    count: number;
    converted: number;
  }>();

  (requests || []).forEach((r) => {
    if (!r.service_city) return;
    const key = `${r.service_city}-${r.service_province}`;
    if (!cityMap.has(key)) {
      cityMap.set(key, {
        city: r.service_city,
        province: r.service_province || '',
        postalCode: r.service_zip || undefined,
        franchise: r.franchise || 'unknown',
        count: 0,
        converted: 0,
      });
    }
    const data = cityMap.get(key)!;
    data.count++;
    if (r.status === 'completed' || r.status === 'converted') data.converted++;
  });

  return Array.from(cityMap.values()).map((city) => ({
    city: city.city,
    province: city.province,
    postalCode: city.postalCode,
    franchise: city.franchise,
    totalRequests: city.count,
    totalValue: city.converted,
    timeAnalytics: { weekly: [], monthly: [], yearly: [] },
  }));
}
